// Best-effort OS / architecture detection for picking a default download.
//
// Detection sources, in order of reliability:
//   1. navigator.userAgentData (Chrome/Edge/Opera) — gives platform + arch
//      via getHighEntropyValues.
//   2. navigator.userAgent string sniffing — fallback for Safari/Firefox.
//
// Apple Silicon caveat: on macOS the UA still says "Intel Mac OS X" for
// compatibility, even on M-series machines. The only reliable signal is
// userAgentData.getHighEntropyValues(['architecture']). For Mac we just
// default to darwin-arm64 — matches the dev team's primary platform and
// what CI builds for. Intel Mac users can switch via the chooser.

import type { Platform } from "./release";

export type { Platform } from "./release";

export interface DetectedOS {
  platform: Platform;
  label: "macOS" | "Linux" | "Windows";
  detectionConfidence: "high" | "medium" | "low";
}

interface UADataLike {
  platform?: string;
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ architecture?: string; bitness?: string; platform?: string }>;
}

function getUAData(): UADataLike | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as Navigator & { userAgentData?: UADataLike })
    .userAgentData;
}

// Synchronous best-effort detection from navigator.userAgent and
// navigator.userAgentData.platform (low-entropy, available without await).
// For arm-vs-x86 disambiguation use detectArchAsync to refine the result.
export function detectOS(): DetectedOS {
  if (typeof navigator === "undefined") {
    return {
      platform: "darwin-arm64",
      label: "macOS",
      detectionConfidence: "low",
    };
  }

  const ua = navigator.userAgent || "";
  const uaData = getUAData();
  const uaPlatform = (uaData?.platform || "").toLowerCase();

  // High-entropy UA Client Hints (Chromium-based browsers).
  if (uaPlatform) {
    if (uaPlatform === "macos") {
      // Default Mac to arm64. detectArchAsync may downgrade to x64 if the
      // architecture hint comes back as 'x86'.
      return {
        platform: "darwin-arm64",
        label: "macOS",
        detectionConfidence: "medium",
      };
    }
    if (uaPlatform === "windows") {
      return {
        platform: "windows-amd64",
        label: "Windows",
        detectionConfidence: "medium",
      };
    }
    if (uaPlatform === "linux") {
      return {
        platform: "linux-amd64",
        label: "Linux",
        detectionConfidence: "medium",
      };
    }
    if (uaPlatform === "ios") {
      // Mobile users browsing the marketing page; offer Mac as a sensible
      // default for the chooser. They'll likely install on a desktop later.
      return {
        platform: "darwin-arm64",
        label: "macOS",
        detectionConfidence: "low",
      };
    }
    if (uaPlatform === "android") {
      return {
        platform: "linux-amd64",
        label: "Linux",
        detectionConfidence: "low",
      };
    }
  }

  // Legacy UA-string sniffing.
  const isArm = /arm64|aarch64/i.test(ua);
  if (/Mac OS X|Macintosh/i.test(ua)) {
    // UA on Mac still claims "Intel Mac OS X" even on Apple Silicon, so we
    // default to arm64. Architecture refinement via detectArchAsync.
    return {
      platform: "darwin-arm64",
      label: "macOS",
      detectionConfidence: "low",
    };
  }
  if (/Windows/i.test(ua)) {
    // Windows on ARM runs the amd64 build under Microsoft Prism emulation,
    // so always serve windows-amd64.
    return {
      platform: "windows-amd64",
      label: "Windows",
      detectionConfidence: "medium",
    };
  }
  if (/Linux|X11/i.test(ua)) {
    return {
      platform: isArm ? "linux-arm64" : "linux-amd64",
      label: "Linux",
      detectionConfidence: "medium",
    };
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return {
      platform: "darwin-arm64",
      label: "macOS",
      detectionConfidence: "low",
    };
  }
  if (/Android/i.test(ua)) {
    return {
      platform: "linux-amd64",
      label: "Linux",
      detectionConfidence: "low",
    };
  }

  // Fall through: default to darwin-arm64. Matches the dev team's primary
  // platform and the source prototype's hardcoded default.
  return {
    platform: "darwin-arm64",
    label: "macOS",
    detectionConfidence: "low",
  };
}

// High-entropy detection. Uses navigator.userAgentData.getHighEntropyValues
// to disambiguate arm vs x86 — the only browser-exposed way to detect
// Intel Mac vs Apple Silicon. Falls back to the initial detection if the
// API is unavailable or rejects.
export async function detectArchAsync(
  initial: DetectedOS,
): Promise<DetectedOS> {
  const uaData = getUAData();
  if (!uaData?.getHighEntropyValues) return initial;
  try {
    const hints = await uaData.getHighEntropyValues(["architecture"]);
    const arch = (hints.architecture || "").toLowerCase();
    if (!arch) return initial;
    const isArm = arch === "arm";

    if (initial.label === "macOS") {
      return {
        ...initial,
        platform: isArm ? "darwin-arm64" : "darwin-arm64",
        // Mac always serves arm64. Intel Macs are out of scope for v1; if
        // arch reports x86 the user can switch via the chooser. We keep
        // platform=darwin-arm64 here rather than introducing an unsupported
        // darwin-amd64 entry.
        detectionConfidence: "high",
      };
    }
    if (initial.label === "Linux") {
      return {
        ...initial,
        platform: isArm ? "linux-arm64" : "linux-amd64",
        detectionConfidence: "high",
      };
    }
    if (initial.label === "Windows") {
      // Windows ARM runs amd64 under Prism emulation, so we always serve
      // windows-amd64 regardless of arch hint.
      return {
        ...initial,
        platform: "windows-amd64",
        detectionConfidence: "high",
      };
    }
    return initial;
  } catch {
    return initial;
  }
}
